import React, { useEffect, useState } from 'react';

import { SvgIcon } from '@material-ui/core';

import { close, down } from '@/assets/icons';
import { AsaphDropdownMenu } from '@/components/shared/AsaphDropdownMenu';
import { Chip } from '@/components/shared/Chip';
import { RoundedButton } from '@/components/shared/RoundedButton';
import { classNames } from '@/helpers/classNames';

import styles from './ReportsFilterBar.module.scss';

type MultiSelectChipProps = {
    label: string;
    options: { label: string; value: string }[];
    selected: string[];
    onToggle: (value: string) => void;
};

const MultiSelectChip: React.FC<MultiSelectChipProps> = ({
    label,
    options,
    selected,
    onToggle,
}) => {
    const [anchorEl, setAnchorEl] = useState<Element | null>(null);

    useEffect(() => {
        if (!anchorEl) return;
        const t = setTimeout(() => window.dispatchEvent(new Event('resize')), 210);
        return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selected]);

    const isActive = selected.length > 0;
    const displayLabel = isActive ? `${label}: ${selected.length}` : label;

    const chevron = <SvgIcon component={down} style={{ fontSize: 14 }} />;

    const panel = (
        <div className={styles['multi-select-panel']}>
            {options.length === 0 ? (
                <div className={styles['multi-select-empty']}>No options</div>
            ) : (
                options.map((opt) => (
                    <div
                        key={opt.value}
                        className={styles['multi-select-row']}
                        onClick={() => onToggle(opt.value)}
                    >
                        <input
                            type="checkbox"
                            readOnly
                            checked={selected.includes(opt.value)}
                        />
                        <span>{opt.label}</span>
                    </div>
                ))
            )}
        </div>
    );

    return (
        <>
            <Chip
                variant="rounded"
                selected={isActive}
                className={isActive ? styles['chip--active'] : styles['chip--default']}
                trailingComponent={chevron}
                onClick={(e) => setAnchorEl(e.currentTarget)}
            >
                {displayLabel}
            </Chip>
            <AsaphDropdownMenu
                open={!!anchorEl}
                anchorEl={anchorEl}
                onClose={() => setAnchorEl(null)}
                items={[]}
                customContent={panel}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                transformOrigin={{ vertical: 'top', horizontal: 'left' }}
            />
        </>
    );
};

export type ReportsFilterBarProps = {
    skillFilter: string[];
    onSkillToggle: (value: string) => void;
    /** Omit to hide the chip entirely */
    skillOptions?: { label: string; value: string }[];
    /** Gender filter: values are gender strings from user profile (e.g. 'male', 'female') */
    genderFilter: string[];
    onGenderToggle: (value: string) => void;
    /** Omit to hide the chip entirely */
    genderOptions?: { label: string; value: string }[];
    /** Generation filter: values are Generation labels from GENERATIONS constant (e.g. 'Gen Z', 'Millennial') */
    generationFilter: string[];
    onGenerationToggle: (value: string) => void;
    /** Omit to hide the chip entirely */
    generationOptions?: { label: string; value: string }[];
    serviceFilter: string[];
    onServiceToggle: (value: string) => void;
    /** Omit to hide the chip entirely */
    serviceOptions?: { label: string; value: string }[];
    onClearAll: () => void;
};

export const ReportsFilterBar: React.FC<ReportsFilterBarProps> = ({
    skillFilter,
    onSkillToggle,
    skillOptions,
    genderFilter,
    onGenderToggle,
    genderOptions,
    generationFilter,
    onGenerationToggle,
    generationOptions,
    serviceFilter,
    onServiceToggle,
    serviceOptions,
    onClearAll,
}) => {
    const hasActiveFilters =
        skillFilter.length > 0 ||
        genderFilter.length > 0 ||
        generationFilter.length > 0 ||
        serviceFilter.length > 0;

    const xIcon = <SvgIcon component={close} style={{ fontSize: 12 }} />;

    return (
        <div className={styles['filter-bar']}>
            <div
                className={classNames([
                    styles['clear-button-wrapper'],
                    hasActiveFilters && styles['clear-button-wrapper--visible'],
                ])}
            >
                <RoundedButton color="default" leadingComponent={xIcon} onClick={onClearAll}>
                    Clear
                </RoundedButton>
            </div>
            {serviceOptions && (
                <MultiSelectChip
                    label="Service"
                    options={serviceOptions}
                    selected={serviceFilter}
                    onToggle={onServiceToggle}
                />
            )}
            {generationOptions && (
                <MultiSelectChip
                    label="Generation"
                    options={generationOptions}
                    selected={generationFilter}
                    onToggle={onGenerationToggle}
                />
            )}
            {genderOptions && (
                <MultiSelectChip
                    label="Gender"
                    options={genderOptions}
                    selected={genderFilter}
                    onToggle={onGenderToggle}
                />
            )}
            {skillOptions && (
                <MultiSelectChip
                    label="Skill"
                    options={skillOptions}
                    selected={skillFilter}
                    onToggle={onSkillToggle}
                />
            )}
        </div>
    );
};
